import { useCallback, useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, CheckCircle2, Download, Copy, KeyRound, Shuffle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { downloadCsv } from "@/lib/exports";
import { useAuth } from "@/context/AuthContext";

const ROLE_RANK = { support_agent: 1, staff: 1, manager: 2, admin: 3 };

function generatePassword(len = 12) {
  const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%";
  let out = "";
  const rand = window.crypto?.getRandomValues
    ? (arr) => window.crypto.getRandomValues(arr)
    : (arr) => { for (let i = 0; i < arr.length; i++) arr[i] = Math.floor(Math.random() * 256); return arr; };
  const buf = rand(new Uint8Array(len));
  for (let i = 0; i < len; i++) out += chars[buf[i] % chars.length];
  return out;
}

const ONBOARDING_STEPS = [
  { key: "profile_completed", label: "Profile completed" },
  { key: "documents_uploaded", label: "Documents uploaded" },
  { key: "training_completed", label: "Training completed" },
  { key: "equipment_assigned", label: "Equipment assigned" },
];

export default function Staff() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [form, setForm] = useState({ email: "", password: generatePassword(), name: "", role: "staff", department: "FigureWeb", avatar: "" });

  const load = useCallback(() => api.get("/users").then(r => setUsers(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name} removed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete user");
    }
  };

  // Roles the current user is allowed to grant (rank <= their own)
  const myRank = ROLE_RANK[me?.role] || 0;
  const grantableRoles = Object.entries(ROLE_RANK).filter(([, rank]) => rank <= myRank).map(([r]) => r);
  const canOnboard = me && (me.role === "admin" || me.role === "manager");

  const openInvite = () => {
    setInviteResult(null);
    setForm({ email: "", password: generatePassword(), name: "", role: "staff", department: "FigureWeb", avatar: "" });
    setOpen(true);
  };

  const create = async () => {
    if (!form.email || !form.name || !form.password) return toast.error("All fields required");
    try {
      await api.post("/users", form);
      setInviteResult({ ...form });
      toast.success("Staff onboarded — share the invite below");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const copyInvite = () => {
    if (!inviteResult) return;
    const text = `You've been added to FiguraidOS.

Login: ${window.location.origin}/login
Email: ${inviteResult.email}
Temp password: ${inviteResult.password}

Please change your password after first login.`;
    navigator.clipboard.writeText(text);
    toast.success("Invite copied to clipboard");
  };

  const toggleOnboard = async (user, key) => {
    const onboarding = { ...(user.onboarding || {}), [key]: !user.onboarding?.[key] };
    await api.patch(`/users/${user.id}`, { onboarding });
    load();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Staff & HR"
        subtitle={`${users.length} team members across FigureWeb, FigureCX, and Telecom`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => downloadCsv("users")} data-testid="export-staff-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            {canOnboard && (
              <Dialog open={open} onOpenChange={(v) => { if (!v) setInviteResult(null); setOpen(v); }}>
                <DialogTrigger asChild>
                  <Button onClick={openInvite} data-testid="add-staff-btn" className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">
                    <Plus className="w-4 h-4 mr-1" /> Onboard Staff
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  {!inviteResult ? (
                    <>
                      <DialogHeader><DialogTitle>Onboard a new team member</DialogTitle></DialogHeader>
                      <div className="space-y-3">
                        <div><Label>Full name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="staff-name-input" /></div>
                        <div><Label>Work email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="staff-email-input" /></div>
                        <div>
                          <Label>Temporary password</Label>
                          <div className="flex gap-2 mt-1">
                            <Input value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="font-mono" />
                            <Button type="button" variant="outline" size="icon" onClick={() => setForm({...form, password: generatePassword()})} data-testid="regen-password-btn">
                              <Shuffle className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-1">Will be shown once after creation — copy it to share with the new hire.</div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label>Role</Label>
                            <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                              <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {grantableRoles.includes("admin") && <SelectItem value="admin">Admin / CEO</SelectItem>}
                                {grantableRoles.includes("manager") && <SelectItem value="manager">Manager</SelectItem>}
                                {grantableRoles.includes("staff") && <SelectItem value="staff">Staff</SelectItem>}
                                {grantableRoles.includes("support_agent") && <SelectItem value="support_agent">Support Agent</SelectItem>}
                              </SelectContent>
                            </Select>
                            <div className="text-[11px] text-slate-500 mt-1">You can only assign roles at or below your own level.</div>
                          </div>
                          <div><Label>Department</Label>
                            <Select value={form.department} onValueChange={v => setForm({...form, department: v})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="FigureWeb">FigureWeb</SelectItem>
                                <SelectItem value="FigureCX">FigureCX</SelectItem>
                                <SelectItem value="Telecom">Telecom</SelectItem>
                        <SelectItem value="FigureAcademy">Figure Academy</SelectItem>
                                <SelectItem value="Executive">Executive</SelectItem>
                              </SelectContent>
                            </Select></div>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={create} data-testid="save-staff-btn" className="bg-brand-aqua hover:bg-brand-aqua-hover text-white">Send invite</Button>
                      </DialogFooter>
                    </>
                  ) : (
                    <>
                      <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" /> Invite ready</DialogTitle></DialogHeader>
                      <div className="space-y-3">
                        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-md px-3 py-2">
                          <strong>Copy these credentials now</strong> — for security, the password is not shown again.
                        </div>
                        <div className="rounded-md border border-slate-200 p-4 bg-slate-50 space-y-2 font-mono text-sm">
                          <div><span className="text-slate-500">Login:</span> {window.location.origin}/login</div>
                          <div><span className="text-slate-500">Email:</span> {inviteResult.email}</div>
                          <div className="flex items-center gap-2">
                            <KeyRound className="w-4 h-4 text-brand-aqua" />
                            <span className="text-slate-500">Password:</span>
                            <span className="font-semibold text-slate-900" data-testid="invite-password-display">{inviteResult.password}</span>
                          </div>
                          <div><span className="text-slate-500">Role:</span> {inviteResult.role} · <span className="text-slate-500">Department:</span> {inviteResult.department}</div>
                        </div>
                        <Button onClick={copyInvite} className="w-full bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="copy-invite-btn">
                          <Copy className="w-4 h-4 mr-2" /> Copy full invite text
                        </Button>
                      </div>
                      <DialogFooter>
                        <Button onClick={() => { setInviteResult(null); setOpen(false); }} variant="outline">Done</Button>
                      </DialogFooter>
                    </>
                  )}
                </DialogContent>
              </Dialog>
            )}
          </div>
        }
      />

      <Tabs defaultValue="directory">
        <TabsList>
          <TabsTrigger value="directory" data-testid="tab-directory">Directory</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="tab-onboarding">Onboarding</TabsTrigger>
        </TabsList>

        <TabsContent value="directory" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {users.map(u => (
              <div key={u.id} className="kpi-card" data-testid={`staff-card-${u.id}`}>
                <div className="flex items-start gap-3">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={u.avatar} alt={u.name} />
                    <AvatarFallback>{u.name?.[0] || "U"}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800">{u.name}</div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <Chip variant="aqua">{u.role?.replace("_", " ")}</Chip>
                      {u.department && <Chip>{u.department}</Chip>}
                    </div>
                  </div>
                  {me?.role === "admin" && u.id !== me.id && (
                    <Button size="sm" variant="ghost" onClick={() => deleteUser(u)}
                            className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                            data-testid={`delete-staff-${u.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                <div className="mt-2 text-[11px] text-slate-400">Joined {formatDate(u.created_at)}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="onboarding" className="mt-4">
          <div className="table-wrap">
            <table className="w-full text-sm">
              <thead className="table-head">
                <tr>
                  <th className="text-left px-4 py-3">Team member</th>
                  {ONBOARDING_STEPS.map(s => <th key={s.key} className="text-left px-4 py-3">{s.label}</th>)}
                  <th className="text-left px-4 py-3">Progress</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const total = ONBOARDING_STEPS.length;
                  const done = ONBOARDING_STEPS.filter(s => u.onboarding?.[s.key]).length;
                  const pct = Math.round((done / total) * 100);
                  return (
                    <tr key={u.id} className="row">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Avatar className="w-7 h-7"><AvatarImage src={u.avatar} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
                          <div><div className="font-medium text-slate-800 text-sm">{u.name}</div><div className="text-xs text-slate-500">{u.department}</div></div>
                        </div>
                      </td>
                      {ONBOARDING_STEPS.map(s => (
                        <td key={s.key} className="px-4 py-3">
                          <Checkbox checked={!!u.onboarding?.[s.key]} onCheckedChange={() => toggleOnboard(u, s.key)}
                                    data-testid={`onboard-${u.id}-${s.key}`} />
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-brand-aqua rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs num text-slate-500 w-8">{pct}%</span>
                          {done === total && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
